class LookbookBanner extends HTMLElement {
  constructor() {
    super();

    this.activeSlideIndex = -1;
    this.activeIndex = -1;
    this.isSheetOpen = false;
    this._mounted = false;
    this._lastTriggerDot = null;
    this._hideTimer = null;
    this._boundSwiper = null;
    this._movedCard = null;
    this._movedCardParent = null;
    this._movedCardNext = null;

    this._onEditorLoad = this._onEditorLoad.bind(this);
    this._onEditorUnload = this._onEditorUnload.bind(this);
    this._onBlockSelect = this._onBlockSelect.bind(this);
    this._onBlockDeselect = this._onBlockDeselect.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onSlideChange = this._onSlideChange.bind(this);
    this._onListScroll = this._onListScroll.bind(this);
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this._bindElements();
    this._bindEvents();
    this._scheduleBindSwiper();

    document.addEventListener('shopify:section:load', this._onEditorLoad);
    document.addEventListener('shopify:section:unload', this._onEditorUnload);
    document.addEventListener('shopify:block:select', this._onBlockSelect);
    document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
    document.addEventListener('click', this._onDocumentClick);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this._onEditorLoad);
    document.removeEventListener('shopify:section:unload', this._onEditorUnload);
    document.removeEventListener('shopify:block:select', this._onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    document.removeEventListener('click', this._onDocumentClick);
    window.removeEventListener('resize', this._onResize);

    if (this.listEl) {
      this.listEl.removeEventListener('scroll', this._onListScroll);
    }

    this._unbindSwiper();
    this._clearHideTimer();
    this._closeSheet();
    this._mounted = false;
  }

  _isMobile() {
    return window.matchMedia('(max-width: 575px)').matches;
  }

  _isDesignMode() {
    return !!(window.Shopify && Shopify.designMode);
  }

  _bindElements() {
    this.dots = Array.from(this.querySelectorAll('[data-hotspot-dot]'));
    this.cards = Array.from(this.querySelectorAll('[data-hotspot-card]'));
    this.listItems = Array.from(this.querySelectorAll('[data-lookbook-list-item]'));
    this.closeButtons = Array.from(this.querySelectorAll('[data-hotspot-close]'));
    this.overlay = this.querySelector('[data-hotspot-overlay]');
    this.swiperEl = this.querySelector('tf-swiper');
    this.prevBtn = this.querySelector('[data-lookbook-prev]');
    this.nextBtn = this.querySelector('[data-lookbook-next]');
    this.currentEl = this.querySelector('.lookbook-banner__counter [data-current]');
    this.listEl = this.querySelector('[data-lookbook-list]');
    this.listThumb = this.querySelector('[data-lookbook-list-thumb]');
  }

  _onListScroll() {
    this._updateListThumb();
  }

  _updateListThumb() {
    if (!this.listEl || !this.listThumb) return;

    const { scrollTop, scrollHeight, clientHeight } = this.listEl;
    if (scrollHeight <= clientHeight) {
      this.listThumb.style.setProperty('--thumb-height', '100%');
      this.listThumb.style.setProperty('--thumb-top', '0%');
      return;
    }

    const thumbHeightPercent = Math.max((clientHeight / scrollHeight) * 100, 8);
    const maxScroll = scrollHeight - clientHeight;
    const scrollPercent = scrollTop / maxScroll;
    const thumbTopPercent = scrollPercent * (100 - thumbHeightPercent);

    this.listThumb.style.setProperty('--thumb-height', `${thumbHeightPercent}%`);
    this.listThumb.style.setProperty('--thumb-top', `${thumbTopPercent}%`);
  }

  // Keep the hovered hotspot's product visible even when the list scrolls
  // (more hotspots than fit in the panel), so the correspondence between the
  // dot and its product is never hidden below the fold.
  _scrollActiveListItemIntoView() {
    if (!this.listEl) return;

    const item = this.listItems.find((item) => item.classList.contains('is-active'));
    if (!item) return;

    const listRect = this.listEl.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    if (itemRect.top >= listRect.top && itemRect.bottom <= listRect.bottom) return;

    const offset = itemRect.top - listRect.top - (listRect.height / 2 - itemRect.height / 2);
    this.listEl.scrollTo({ top: this.listEl.scrollTop + offset, behavior: 'smooth' });
  }

  _getSwiper() {
    return this.swiperEl?.el?.swiper || this.swiperEl?._swiper || null;
  }

  _scheduleBindSwiper(retries = 20) {
    const swiper = this._getSwiper();
    if (swiper) {
      this._bindSwiper(swiper);
      return;
    }
    if (retries <= 0) return;
    setTimeout(() => this._scheduleBindSwiper(retries - 1), 150);
  }

  _bindSwiper(swiper) {
    if (this._boundSwiper === swiper) return;
    this._unbindSwiper();
    this._boundSwiper = swiper;
    swiper.on('slideChange', this._onSlideChange);
    this._updateCounter();
  }

  _unbindSwiper() {
    if (!this._boundSwiper) return;
    try {
      this._boundSwiper.off('slideChange', this._onSlideChange);
    } catch (e) { }
    this._boundSwiper = null;
  }

  _onSlideChange() {
    this._deactivate();
    this._updateCounter();
  }

  _updateCounter() {
    if (!this.currentEl) return;
    const swiper = this._getSwiper();
    if (!swiper) return;
    const current = (swiper.realIndex ?? swiper.activeIndex ?? 0) + 1;
    this.currentEl.textContent = current;
  }

  _syncSwiperTo(slideIndex) {
    const swiper = this._getSwiper();
    if (!swiper || typeof swiper.slideTo !== 'function') return;
    swiper.slideTo(slideIndex);
  }

  _bindEvents() {
    this.dots.forEach((dot) => {
      const index = parseInt(dot.dataset.index, 10);
      const slideIndex = parseInt(dot.dataset.slideIndex, 10);
      if (Number.isNaN(index) || Number.isNaN(slideIndex)) return;

      dot.addEventListener('pointerenter', () => {
        if (this._isDesignMode()) return;
        if (this._isMobile()) return;
        this._activate(slideIndex, index);
      });
      dot.addEventListener('focus', () => {
        if (this._isDesignMode()) return;
        if (this._isMobile()) return;
        this._activate(slideIndex, index);
      });
      dot.addEventListener('pointerleave', () => {
        if (this._isDesignMode()) return;
        if (!this._isMobile()) this._scheduleDeactivate();
      });
      dot.addEventListener('blur', () => {
        if (this._isDesignMode()) return;
        if (!this._isMobile()) this._scheduleDeactivate();
      });

      dot.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this._isDesignMode()) return;
        if (this._isMobile()) {
          this._lastTriggerDot = dot;
          this._activate(slideIndex, index);
          this._openSheet();
        } else {
          this._activate(slideIndex, index);
        }
      });
    });

    this.cards.forEach((card) => {
      card.addEventListener('pointerenter', () => {
        if (!this._isMobile()) this._clearHideTimer();
      });
      card.addEventListener('pointerleave', () => {
        if (!this._isMobile()) this._scheduleDeactivate();
      });
      card.addEventListener('click', (event) => event.stopPropagation());
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this._closeSheet());
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this._closeSheet());
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this._getSwiper()?.slidePrev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this._getSwiper()?.slideNext());
    }

    if (this.listEl) {
      this.listEl.addEventListener('scroll', this._onListScroll, { passive: true });
      this._updateListThumb();
    }

    this.addEventListener('keydown', this._onKeydown);
  }

  _onDocumentClick(event) {
    if (this._isMobile()) return;
    if (this.activeIndex === -1) return;
    if (this.contains(event.target) && event.target.closest('[data-hotspot-dot], [data-hotspot-card]')) return;
    this._deactivate();
  }

  _onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.isSheetOpen) this._closeSheet();
    else this._deactivate();
  }

  _onResize() {
    if (this.isSheetOpen && !this._isMobile()) this._closeSheet();
    if (this._movedCard && this._movedCard.classList.contains('lookbook-banner__card--floating')) {
      this._deactivate();
    }
  }

  _clearHideTimer() {
    if (this._hideTimer) clearTimeout(this._hideTimer);
    this._hideTimer = null;
  }

  _scheduleDeactivate() {
    this._clearHideTimer();
    this._hideTimer = setTimeout(() => this._deactivate(), 150);
  }

  _activate(slideIndex, index) {
    this._clearHideTimer();
    if (this.activeSlideIndex === slideIndex && this.activeIndex === index) return;

    this.activeSlideIndex = slideIndex;
    this.activeIndex = index;
    this.classList.add('has-active-hotspot');
    this._render();
    if (!this._isMobile()) this._floatActiveCard();
    this._scrollActiveListItemIntoView();
  }

  _deactivate() {
    this._clearHideTimer();
    if (this.activeIndex === -1) return;

    this.activeSlideIndex = -1;
    this.activeIndex = -1;
    this.classList.remove('has-active-hotspot');
    this._restoreMovedCard();
    this._render();
  }

  _render() {
    this.dots.forEach((dot) => {
      const isActive =
        parseInt(dot.dataset.slideIndex, 10) === this.activeSlideIndex &&
        parseInt(dot.dataset.index, 10) === this.activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    this.cards.forEach((card) => {
      const isActive =
        parseInt(card.dataset.slideIndex, 10) === this.activeSlideIndex &&
        parseInt(card.dataset.index, 10) === this.activeIndex;
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.listItems.forEach((item) => {
      const isActive =
        parseInt(item.dataset.slideIndex, 10) === this.activeSlideIndex &&
        parseInt(item.dataset.index, 10) === this.activeIndex;
      item.classList.toggle('is-active', isActive);
    });
  }

  _openSheet() {
    this.isSheetOpen = true;
    this.classList.add('is-sheet-open');
    if (this.overlay) this.overlay.hidden = false;
    document.body.classList.add('overflow-hidden');
    this._moveActiveCardToBody();
  }

  _closeSheet() {
    if (!this.isSheetOpen) return;

    this.isSheetOpen = false;
    this.classList.remove('is-sheet-open');
    if (this.overlay) this.overlay.hidden = true;
    document.body.classList.remove('overflow-hidden');
    this._restoreMovedCard();
    this._deactivate();

    if (this._lastTriggerDot) {
      this._lastTriggerDot.focus();
      this._lastTriggerDot = null;
    }
  }

  // Swiper applies a transform on .swiper-wrapper, which makes it the
  // containing block for any `position: fixed` descendant (per spec), and
  // `.lookbook-banner__media`/`.swiper` both clip overflow. Reparent the
  // active card to <body> so it's truly fixed to the viewport, renders above
  // the overlay, and is never cropped by an ancestor's `overflow: hidden`.
  _moveActiveCardToBody() {
    this._restoreMovedCard();

    const card = this.cards.find((card) => card.classList.contains('is-active'));
    if (!card) return;

    this._movedCard = card;
    this._movedCardParent = card.parentNode;
    this._movedCardNext = card.nextSibling;
    card.classList.add('lookbook-banner__card--sheet');
    document.body.appendChild(card);
  }

  // Desktop hover popover: same reparenting trick as the mobile sheet, but
  // positioned next to the dot instead of docked to the bottom of the screen.
  _floatActiveCard() {
    const card = this.cards.find((card) => card.classList.contains('is-active'));
    if (!card || this._movedCard === card) return;

    const dot = this.dots.find(
      (dot) =>
        parseInt(dot.dataset.slideIndex, 10) === this.activeSlideIndex &&
        parseInt(dot.dataset.index, 10) === this.activeIndex
    );
    if (!dot) return;

    this._restoreMovedCard();

    this._movedCard = card;
    this._movedCardParent = card.parentNode;
    this._movedCardNext = card.nextSibling;
    card.classList.add('lookbook-banner__card--floating');
    document.body.appendChild(card);
    this._positionFloatingCard(dot, card);
  }

  _positionFloatingCard(dot, card) {
    const gap = 16;
    const edge = 16;
    const dotRect = dot.getBoundingClientRect();
    const cardWidth = card.offsetWidth || 280;
    const cardHeight = card.offsetHeight || 112;

    let alignRight = false;
    let left = dotRect.right + gap;
    if (left + cardWidth > window.innerWidth - edge) {
      left = dotRect.left - gap - cardWidth;
      alignRight = true;
    }
    left = Math.max(edge, Math.min(left, window.innerWidth - cardWidth - edge));

    let top = dotRect.top + dotRect.height / 2 - cardHeight / 2;
    top = Math.max(edge, Math.min(top, window.innerHeight - cardHeight - edge));

    card.style.position = 'fixed';
    card.style.transform = 'none';
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
    card.classList.toggle('lookbook-banner__card--align-right', alignRight);
    card.classList.toggle('lookbook-banner__card--align-left', !alignRight);
  }

  _restoreMovedCard() {
    if (!this._movedCard) return;

    const card = this._movedCard;
    const parent = this._movedCardParent;
    const next = this._movedCardNext;
    this._movedCard = null;
    this._movedCardParent = null;
    this._movedCardNext = null;

    card.classList.remove('lookbook-banner__card--sheet', 'lookbook-banner__card--floating');
    card.style.position = '';
    card.style.left = '';
    card.style.top = '';
    card.style.transform = '';
    if (!parent) return;

    if (next && next.parentNode === parent) {
      parent.insertBefore(card, next);
    } else {
      parent.appendChild(card);
    }
  }

  _onEditorLoad(event) {
    if (!event?.target || !event.target.contains(this)) return;
    this._bindElements();
    this._bindEvents();
    this._deactivate();
    this._scheduleBindSwiper();
  }

  _onEditorUnload(event) {
    if (!event?.target || !event.target.contains(this)) return;
    this._closeSheet();
  }

  // Only sync the swiper to the selected block's slide. Don't auto-float the
  // mini-card popup here: Shopify re-fires `shopify:block:select` on every
  // section re-render (e.g. each tick while dragging a position slider),
  // which would otherwise keep re-opening the popup over the editor.
  _onBlockSelect(event) {
    if (!event?.target || !this.contains(event.target)) return;

    const slideEl = event.target.closest('.swiper-slide');
    const slideIndex = slideEl ? parseInt(slideEl.dataset.slideIndex, 10) : NaN;

    if (!Number.isNaN(slideIndex)) this._syncSwiperTo(slideIndex);
  }

  _onBlockDeselect(event) {
    if (!event?.target || !this.contains(event.target)) return;
    this._deactivate();
  }
}

if (!customElements.get('lookbook-banner')) {
  customElements.define('lookbook-banner', LookbookBanner);
}
